import { TasksSource } from "@jugl-web/rest-api/tasks";
import { SpecificOrAnyString, useTranslations } from "@jugl-web/utils";
import { useCallback, useMemo } from "react";
import { TaskDefaultFieldId } from "../types";
import { extractBoardIdFromTasksSource, getTasksSourceInfo } from "../utils";
import { useTaskCustomFields } from "./useTaskCustomFields";
import { useTaskFieldList } from "./useTaskFieldList";
import { useTaskLabels } from "./useTaskLabels";

export enum TaskGroupingType {
  default = "default",
  status = "status",
  date = "date",
  labels = "labels",
  priority = "priority",
  customDropdownField = "customDropdownField",
  assignee = "assignee",
  reportee = "reportee",
  customer = "customer",
  board = "board",
}

export type TaskGroupingId = SpecificOrAnyString<
  | `${TaskGroupingType.default}`
  | `${TaskGroupingType.reportee}`
  | Extract<
      TaskDefaultFieldId,
      | "due_date"
      | "assignee"
      | "board"
      | "priority"
      | "customer"
      | "label"
      | "status"
    >
>;

export type TaskGrouping = {
  id: TaskGroupingId;
  fullTitle: string;
  shortTitle: string;
  isNotSelectableByUser?: boolean;
} & (
  | { type: Exclude<TaskGroupingType, "customDropdownField"> }
  | {
      type: TaskGroupingType.customDropdownField;
      customDropdownFieldId: string;
    }
);

interface UseTaskGroupingOptions {
  entityId: string;
  source: TasksSource;
}

export const useTaskGrouping = ({
  entityId,
  source,
}: UseTaskGroupingOptions) => {
  const boardId = extractBoardIdFromTasksSource(source) || null;

  const { areLabelsSupported } = useTaskLabels({ entityId });
  const { boardCustomFields } = useTaskCustomFields({ entityId, boardId });
  const { isFieldEnabled } = useTaskFieldList({ entityId, boardId });

  const { t } = useTranslations();

  const customDropdownFields = useMemo(
    () => boardCustomFields.filter((item) => item.type === "dropdown"),
    [boardCustomFields]
  );

  const allGroupings = useMemo<TaskGrouping[]>(
    () => [
      {
        id: "default",
        fullTitle: t({
          id: "tasks-page.default-view",
          defaultMessage: "Default View",
        }),
        shortTitle: t({
          id: "tasks-page.default-view-view-by-title",
          defaultMessage: "Default",
        }),
        type: TaskGroupingType.default,
      },
      {
        id: "due_date",
        fullTitle: t({
          id: "tasks-page.date-view",
          defaultMessage: "Date View",
        }),
        shortTitle: t({
          id: "tasks-page.date-view-view-by-title",
          defaultMessage: "Date",
        }),
        type: TaskGroupingType.date,
      },
      {
        id: "label",
        fullTitle: t({
          id: "tasks-page.labels-view",
          defaultMessage: "Labels View",
        }),
        shortTitle: t({
          id: "tasks-page.labels-view-view-by-title",
          defaultMessage: "Label",
        }),
        type: TaskGroupingType.labels,
      },
      {
        id: "priority",
        fullTitle: t({
          id: "tasks-page.priority-view",
          defaultMessage: "Priority View",
        }),
        shortTitle: t({
          id: "tasks-page.priority-view-view-by-title",
          defaultMessage: "Priority",
        }),
        type: TaskGroupingType.priority,
      },
      {
        id: "status",
        fullTitle: t({
          id: "tasks-page.status-view",
          defaultMessage: "Status View",
        }),
        shortTitle: t({
          id: "tasks-page.status-view-view-by-title",
          defaultMessage: "Status",
        }),
        type: TaskGroupingType.status,
      },
      {
        id: "assignee",
        fullTitle: t({
          id: "tasks-page.assignee-view",
          defaultMessage: "Assignee View",
        }),
        shortTitle: t({
          id: "tasks-page.assignee-view-view-by-title",
          defaultMessage: "Assignee",
        }),
        type: TaskGroupingType.assignee,
      },
      {
        id: "reportee",
        fullTitle: t({
          id: "tasks-page.reportee-view",
          defaultMessage: "Reportee View",
        }),
        shortTitle: t({
          id: "tasks-page.reportee-view-view-by-title",
          defaultMessage: "Reportee",
        }),
        type: TaskGroupingType.reportee,
        isNotSelectableByUser: true,
      },
      {
        id: "board",
        fullTitle: t({
          id: "tasks-page.board-view",
          defaultMessage: "Board View",
        }),
        shortTitle: t({
          id: "tasks-page.board-view-view-by-title",
          defaultMessage: "Board",
        }),
        type: TaskGroupingType.board,
      },
      {
        id: "customer",
        fullTitle: t({
          id: "tasks-page.customer-view",
          defaultMessage: "Customer View",
        }),
        shortTitle: t({
          id: "tasks-page.customer-view-view-by-title",
          defaultMessage: "Customer",
        }),
        type: TaskGroupingType.customer,
      },
      ...customDropdownFields.map<TaskGrouping>((item) => ({
        id: item.id,
        fullTitle: item.name,
        shortTitle: item.name,
        type: TaskGroupingType.customDropdownField,
        customDropdownFieldId: item.id,
      })),
    ],
    [customDropdownFields, t]
  );

  const relevantGroupings = useMemo(() => {
    const sourceInfo = getTasksSourceInfo(source);

    return allGroupings.filter((grouping) => {
      let isValid = true;

      if (grouping.type === TaskGroupingType.labels) {
        isValid = areLabelsSupported;
      } else if (grouping.type === TaskGroupingType.board) {
        isValid = !sourceInfo.isUserBoardTasks;
      } else if (grouping.type === TaskGroupingType.customer) {
        isValid = !sourceInfo.isCustomerTasks;
      }

      if (!isValid) {
        return false;
      }

      return isFieldEnabled(grouping.id);
    });
  }, [allGroupings, areLabelsSupported, isFieldEnabled, source]);

  const userSelectableGroupings = useMemo(
    () =>
      relevantGroupings.filter((grouping) => !grouping.isNotSelectableByUser),
    [relevantGroupings]
  );

  const getGroupingById = useCallback(
    (id: string) => {
      const foundGrouping = relevantGroupings.find((item) => item.id === id);

      if (!foundGrouping) {
        // oxlint-disable-next-line no-console
        console.warn(`View with id: "${id}" not found`);
        return relevantGroupings[0];
      }

      return foundGrouping;
    },
    [relevantGroupings]
  );

  return {
    userSelectableGroupings,
    getGroupingById,
  };
};
